/**
 * MiningSystem.js
 *
 * Phase 8 — Mineral mining for the KRAKEN 3D gameplay.
 *
 * Owns:
 *   - Deterministic tile-keyed deposit spawning (drives ALL world placement
 *     from (tileX, tileZ, bodySeed) so two players visiting the same body
 *     see identical deposits).
 *   - Element pool per body type, weighted by rarity from the catalog the
 *     backend serves at /api/mining/elements.
 *   - Mining tool: right-click hold ~2s with reticle on a deposit; player
 *     reticle must remain near the deposit for the duration. Awards ore to
 *     cargo and shrinks/destroys the deposit.
 *   - Mining-beam + spark visuals.
 *   - Reticle-cast / aim utility so the HUD can show "MINEABLE — hold RMB".
 *
 * Composed into GameScene — call mining.tick(dt) every frame, and notify it
 * of right-mouse-button state via mining.setMining(true|false).
 *
 * Pure Babylon.js + plain JS — no React imports.
 */

import {
  Vector3,
  Color3,
  Color4,
  MeshBuilder,
  StandardMaterial,
  TransformNode,
} from '@babylonjs/core';

// --- Tunables ---

const TILE_SIZE = 140;           // world meters per deposit tile (smaller = denser)
const MAX_DEPOSITS = 28;         // hard cap concurrent deposit meshes
const MINE_DURATION = 2.0;       // seconds to hold RMB to extract 1 unit
const MINE_REACH = 6.5;          // meters: max range to be "MINEABLE"
const MINE_AIM_RADIUS = 2.0;     // meters: reticle/deposit hit tolerance
const SAFE_SHIP_RADIUS = 14;     // no deposits this close to the ship spawn
const BEACON_HEIGHT = 5.5;       // tall emissive vertical beacon so deposits are visible from afar

// Biome → element pool (symbols). Heavier weighting on common things you'd
// expect in that environment; rarer elements layered in by rarity weights.
const BIOME_POOLS = {
  Terrestrial: ['Fe', 'Si', 'Al', 'Cu', 'Ti', 'Ni', 'Au', 'Ag', 'Co', 'Mn'],
  Ocean:       ['Mg', 'Na', 'Ca', 'K',  'I',  'S',  'Cl', 'Au', 'Ag'],
  Lava:        ['Fe', 'Ni', 'Cr', 'V',  'W',  'Mo', 'Ti', 'Pt', 'Ir', 'U'],
  Dwarf:       ['Si', 'Fe', 'Mg', 'Al', 'Ti', 'Ni', 'Co'],
  Moon:        ['Si', 'Fe', 'Ti', 'Al', 'He', 'O',  'Mg', 'Pt', 'Os'],
  'Ice Giant': ['He', 'H',  'N',  'Ne', 'Ar', 'Kr', 'Xe', 'Li', 'Be'],
  'Gas Giant': ['H',  'He', 'Ne', 'Ar', 'Kr'],
  Default:     ['Fe', 'Si', 'Al', 'Cu', 'Ti', 'Au'],
};

// How many deposits a tile may spawn, scaled by body type richness
const BIOME_DENSITY = {
  Terrestrial: 1.8,
  Ocean: 1.1,
  Lava: 2.0,
  Dwarf: 1.6,
  Moon: 1.5,
  'Ice Giant': 1.0,
  'Gas Giant': 0.4,
  Default: 1.4,
};

// Visual rarity colors
const RARITY_COLOR = {
  Common:     '#9ca3af',
  Uncommon:   '#86efac',
  Rare:       '#60a5fa',
  'Ultra Rare': '#c084fc',
  Legendary:  '#fbbf24',
  Mythic:     '#f43f5e',
};

const RARITY_TIER = {
  Common: 1, Uncommon: 1, Rare: 2, 'Ultra Rare': 2, Legendary: 3, Mythic: 3,
};

function hash2(x, y, seed) {
  let h = (x * 374761393 + y * 668265263 + seed) | 0;
  h = (h ^ (h >>> 13)) >>> 0;
  h = Math.imul(h, 1274126177);
  h = (h ^ (h >>> 16)) >>> 0;
  return (h >>> 0) / 4294967295;
}

function hexToColor3(hex) {
  const h = (hex || '#888888').replace('#', '');
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;
  return new Color3(r, g, b);
}

export class MiningSystem {
  constructor(scene, opts = {}) {
    this.scene = scene;
    this.opts = opts;
    this.getPlayerPos = opts.getPlayerPos;
    this.getPlayerForward = opts.getPlayerForward;
    this.getCameraPos = opts.getCameraPos;
    this.getTerrainHeight = opts.getTerrainHeight || (() => 0);
    this.getBodyType = opts.getBodyType || (() => 'Default');
    this.getBodySeed = opts.getBodySeed || (() => 1337);
    this.isMiningAllowed = opts.isMiningAllowed || (() => true);
    this.getInventory = opts.getInventory || (() => null);
    this.getElements = opts.getElements || (() => null);
    this.getScannedElementNames = opts.getScannedElementNames || (() => []);
    this.onExtract = opts.onExtract || (() => {});
    this.onMiningState = opts.onMiningState || (() => {});
    this._yieldMult = 1;

    this._depositParent = new TransformNode('depositsRoot', scene);
    this._deposits = new Map();          // id -> deposit
    this._depleted = new Set();          // ids that have been fully mined (never respawn)
    this._tileCache = new Map();         // tileKey -> [deposit defs]
    this._lastTileCenter = null;

    // Mineable giant boulders (registered by GameScene from the feature
    // scatter). Richer yield than crystal deposits. Keyed by world-rounded id.
    this._boulders = new Map();          // id -> boulder deposit
    this._minedBoulders = new Set();     // depleted boulder ids (session)
    this._boulderUnitsPerExtract = 2;    // boulders give more ore per pull

    this._mining = false;
    this._activeDeposit = null;
    this._mineProgress = 0;
    this._beam = null;
    this._beamMat = null;
    this._lastEmittedTarget = null;

    this._emitMining();
  }

  // -------- Public API --------

  /** Set/unset the right-mouse-button "mining intent" from the React layer. */
  setMining(active) {
    this._mining = !!active;
  }

  /** Phase 1 — mining-laser module multiplies ore yield per pull. */
  setYieldMult(m) {
    this._yieldMult = Math.max(1, Number(m) || 1);
  }

  /**
   * Called by GameScene when the element catalog arrives (or changes).
   * Invalidates the tile cache so the next tick re-streams deposits with
   * real element data instead of the empty placeholder state.
   */
  resetCache() {
    this._tileCache.clear();
    this._lastTileCenter = null;
  }

  /**
   * Register the set of mineable giant boulders for the current surface patch.
   * Called by GameScene each time it (re)scatters surface features. Boulders
   * yield richer ore than scattered crystal nodes and reuse the same RMB-hold
   * mining mechanic. Requires the body to have been scanned (same gate as
   * crystal deposits). `list` = [{ id, pos: Vector3, radius, mesh }].
   */
  setBoulderTargets(list) {
    const incoming = new Set((list || []).map((b) => b.id));
    // Drop boulders no longer in the current patch.
    for (const [id, b] of Array.from(this._boulders.entries())) {
      if (!incoming.has(id)) {
        try { b.marker?.dispose(); } catch (_e) { /* noop */ }
        if (this._activeDeposit === b) this._endMining(false);
        this._boulders.delete(id);
      }
    }
    for (const item of (list || [])) {
      if (!item || !item.pos) continue;
      if (this._minedBoulders.has(item.id)) {
        // Already mined out this session — keep it hidden.
        try { item.mesh?.setEnabled(false); } catch (_e) { /* noop */ }
        continue;
      }
      const existing = this._boulders.get(item.id);
      if (existing) {
        existing.mesh = item.mesh;          // refresh mesh ref after a rebuild
        existing.pos = item.pos.clone();
        existing.radius = item.radius;
        continue;
      }
      const element = this._chooseBoulderElement(item.id);
      if (!element) continue;               // body not scanned yet → not mineable
      // Yield scales with rock size: small rocks give a little, monoliths a lot.
      const units = Math.max(2, Math.min(40, Math.round(2 + (item.radius || 1) * 2.2)));
      this._boulders.set(item.id, {
        id: item.id,
        pos: item.pos.clone(),
        radius: item.radius,
        mesh: item.mesh,
        element,
        isBoulder: true,
        totalUnits: units,
        remainingUnits: units,
        marker: null,
      });
    }
  }

  _makeBoulderMarker(pos, radius, element) {
    const color = hexToColor3(RARITY_COLOR[element.rarity] || '#9ca3af');
    const vein = MeshBuilder.CreatePolyhedron('boulderVein', { type: 1, size: 0.55 }, this.scene);
    const mat = new StandardMaterial('boulderVeinMat', this.scene);
    mat.disableLighting = true;
    mat.emissiveColor = color.scale(1.4);
    mat.specularColor = new Color3(0, 0, 0);
    mat.fogEnabled = true;
    vein.material = mat;
    vein.isPickable = false;
    vein.position = new Vector3(pos.x, pos.y + radius * 0.55, pos.z);
    vein.parent = this._depositParent;
    return vein;
  }

  _chooseBoulderElement(id) {
    const elements = this.getElements?.();
    if (!elements || elements.length === 0) return null;
    const scannedNames = new Set(this.getScannedElementNames?.() || []);
    if (scannedNames.size === 0) return null;
    const pool = elements.filter((e) => scannedNames.has(e.name));
    if (pool.length === 0) return null;
    const bodyType = this.getBodyType();
    const biomeSet = new Set(BIOME_POOLS[bodyType] || BIOME_POOLS.Default);
    // Boulders skew toward better ore than scattered surface nodes.
    const weights = pool.map((e) => {
      let w;
      switch (e.rarity) {
        case 'Common': w = 5; break;
        case 'Uncommon': w = 6; break;
        case 'Rare': w = 4; break;
        case 'Ultra Rare': w = 2.2; break;
        case 'Legendary': w = 1.0; break;
        case 'Mythic': w = 0.4; break;
        default: w = 1;
      }
      if (biomeSet.has(e.symbol)) w *= 3;
      return w;
    });
    const total = weights.reduce((a, b) => a + b, 0);
    let h = 0;
    for (let i = 0; i < id.length; i++) h = (Math.imul(h, 31) + id.charCodeAt(i)) >>> 0;
    const pick = ((h % 100000) / 100000) * total;
    let acc = 0;
    for (let i = 0; i < pool.length; i++) {
      acc += weights[i];
      if (pick <= acc) return pool[i];
    }
    return pool[pool.length - 1];
  }

  tick(dt) {
    if (!this.isMiningAllowed()) {
      // If RMB was held when we lost focus / entered ship, abort the mine.
      if (this._activeDeposit) this._endMining(false);
      return;
    }

    // Find the rock/boulder currently under the reticle, if any
    const aimed = this._reticleTarget();

    // Mining state machine
    if (this._mining && aimed) {
      if (this._activeDeposit !== aimed.deposit) {
        // Switched targets while holding → restart progress on the new one
        this._activeDeposit = aimed.deposit;
        this._mineProgress = 0;
        this._spawnBeam();
      }
      this._mineProgress += dt;
      // Update beam endpoint each tick
      this._updateBeam(aimed.deposit);
      if (this._mineProgress >= MINE_DURATION) {
        this._completeExtract(aimed.deposit);
        this._mineProgress = 0;
      }
    } else if (this._activeDeposit) {
      // RMB released or aim left target → cancel
      this._endMining(false);
    }

    this._emitMining(aimed);
  }

  getDepositPois() {
    // Mining now comes exclusively from rock/boulder objects (no crystal
    // nodes). Surface that has many rocks, so the radar shows only the nearest
    // ~30 mineable rocks within range to stay readable.
    const p = this.getPlayerPos?.();
    const RANGE = 160;
    const list = [];
    for (const b of this._boulders.values()) {
      if (b.remainingUnits <= 0) continue;
      const d = p ? Math.hypot(p.x - b.pos.x, p.z - b.pos.z) : 0;
      if (p && d > RANGE) continue;
      list.push({
        id: `bld_${b.id}`,
        kind: 'deposit',
        x: b.pos.x,
        z: b.pos.z,
        label: b.element.symbol,
        _d: d,
      });
    }
    list.sort((a, c) => a._d - c._d);
    return list.slice(0, 30).map(({ _d, ...rest }) => rest);
  }

  dispose() {
    if (this._beam) try { this._beam.dispose(); } catch (_e) { /* noop */ }
    for (const d of this._deposits.values()) this._destroyDeposit(d);
    this._deposits.clear();
    for (const b of this._boulders.values()) {
      try { b.marker?.dispose(); } catch (_e) { /* noop */ }
    }
    this._boulders.clear();
    try { this._depositParent.dispose(); } catch (_e) { /* noop */ }
  }

  /** Public — returns the world-space position of the currently-drilled deposit
   *  (or null when no extraction is active). Used by EffectsSystem for sparks. */
  getActiveDepositPos() {
    return this._activeDeposit?.pos ? this._activeDeposit.pos.clone() : null;
  }

  // -------- Internals --------

  _emitMining(aimed) {
    // Skip if nothing meaningful changed (cheap stringify of state)
    const target = aimed ? {
      id: aimed.deposit.id,
      symbol: aimed.deposit.element.symbol,
      name: aimed.deposit.element.name,
      rarity: aimed.deposit.element.rarity,
      yield: aimed.deposit.remainingUnits,
      distance: aimed.distance,
    } : null;
    const snapshot = {
      active: !!this._activeDeposit,
      progress: this._activeDeposit ? (this._mineProgress / MINE_DURATION) : 0,
      target,
      elementName: this._activeDeposit?.element?.name || target?.name || null,
    };
    const sig = JSON.stringify(snapshot);
    if (sig === this._lastEmittedTarget) return;
    this._lastEmittedTarget = sig;
    try { this.onMiningState(snapshot); } catch (_e) { /* noop */ }
  }

  /** Returns the deposit/boulder currently in the reticle "cone" within reach. */
  _reticleTarget() {
    const playerPos = this.getCameraPos?.() || this.getPlayerPos?.();
    const fwd = this.getPlayerForward?.();
    if (!playerPos || !fwd) return null;
    const dir = fwd.normalize();
    let best = null;
    let bestDist = Infinity;
    const consider = (obj, reach, aim) => {
      const toD = obj.pos.subtract(playerPos);
      const proj = Vector3.Dot(toD, dir);
      if (proj <= 0) return;                       // behind the player
      const dist = toD.length();
      if (dist > reach) return;
      const closest = playerPos.add(dir.scale(proj));
      const perp = Vector3.Distance(closest, obj.pos);
      if (perp > aim) return;
      if (dist < bestDist) { best = obj; bestDist = dist; }
    };
    for (const d of this._deposits.values()) {
      consider(d, MINE_REACH, MINE_AIM_RADIUS + 0.4);
    }
    // Boulders are large, so scale reach + aim tolerance by their radius.
    for (const b of this._boulders.values()) {
      consider(b, MINE_REACH + b.radius, b.radius * 0.85 + MINE_AIM_RADIUS);
    }
    if (!best) return null;
    return { deposit: best, distance: bestDist };
  }

  _spawnBeam() {
    if (this._beam) { try { this._beam.dispose(); } catch (_e) { /* noop */ } }
    const beam = MeshBuilder.CreateCylinder('mineBeam', {
      height: 1, diameter: 0.12, tessellation: 8, updatable: false,
    }, this.scene);
    const mat = new StandardMaterial('mineBeamMat', this.scene);
    mat.disableLighting = true;
    mat.emissiveColor = new Color3(0.6, 1.0, 0.7);
    mat.fogEnabled = false;
    mat.alpha = 0.85;
    beam.material = mat;
    beam.isPickable = false;
    beam.parent = this._depositParent;
    this._beam = beam;
    this._beamMat = mat;
  }

  _updateBeam(deposit) {
    if (!this._beam) return;
    const origin = this.getCameraPos?.() || this.getPlayerPos?.();
    if (!origin) return;
    const target = deposit.pos;
    const dir = target.subtract(origin);
    const len = Math.max(0.05, dir.length());
    const mid = origin.add(dir.scale(0.5));
    this._beam.position = mid;
    // Scale Y axis (cylinder's height axis) to length
    this._beam.scaling = new Vector3(1, len, 1);
    // Orient beam from origin to target
    this._beam.lookAt(target);
    // Cylinder default points along Y; rotate -90 around X so it lies along the look direction
    this._beam.addRotation(Math.PI / 2, 0, 0);
    // Pulse alpha for a "drilling" feel
    if (this._beamMat) {
      this._beamMat.alpha = 0.55 + 0.35 * (0.5 + 0.5 * Math.sin(performance.now() * 0.02));
    }
  }

  _endMining(/* completed */) {
    this._activeDeposit = null;
    this._mineProgress = 0;
    if (this._beam) { try { this._beam.dispose(); } catch (_e) { /* noop */ } this._beam = null; }
  }

  _completeExtract(deposit) {
    const baseUnits = deposit.isBoulder ? this._boulderUnitsPerExtract : 1;
    const units = Math.max(baseUnits, Math.round(baseUnits * (this._yieldMult || 1)));
    deposit.remainingUnits = Math.max(0, deposit.remainingUnits - baseUnits);
    if (deposit.isBoulder) {
      // Shrink the boulder mesh as it's mined (preserve its base scaling).
      if (deposit.mesh) {
        if (!deposit._baseScale) deposit._baseScale = deposit.mesh.scaling.clone();
        const s = Math.max(0, deposit.remainingUnits / deposit.totalUnits);
        deposit.mesh.scaling = deposit._baseScale.scale(0.6 + 0.4 * s);
      }
    } else if (deposit.mesh) {
      const s = Math.max(0.45, deposit.remainingUnits / deposit.totalUnits);
      deposit.mesh.scaling = new Vector3(s, s, s);
    }
    // Report extraction to React (it does the inventory mutation + capacity check)
    try {
      this.onExtract({
        deposit: {
          id: deposit.id,
          element: deposit.element,    // {symbol, name, rarity, value_per_kg}
        },
        unitsExtracted: units,
      });
    } catch (_e) { /* noop */ }

    if (deposit.remainingUnits <= 0) {
      if (deposit.isBoulder) {
        this._minedBoulders.add(deposit.id);
        try { deposit.marker?.dispose(); } catch (_e) { /* noop */ }
        try { deposit.mesh?.setEnabled(false); } catch (_e) { /* noop */ }
        if (this._activeDeposit === deposit) this._endMining(false);
        this._boulders.delete(deposit.id);
      } else {
        this._depleted.add(deposit.id);     // permanently retire — never respawn this deposit
        this._destroyDeposit(deposit);
        this._deposits.delete(deposit.id);
      }
    }
    // Force a re-emit so the HUD sees the new yield / completion pulse
    this._lastEmittedTarget = null;
  }

  // -------- Deposit streaming --------

  _streamDeposits() {
    const p = this.getPlayerPos?.();
    if (!p) return;
    const cx = Math.floor(p.x / TILE_SIZE);
    const cz = Math.floor(p.z / TILE_SIZE);
    const key = `${cx},${cz}`;
    if (this._lastTileCenter === key) return;
    this._lastTileCenter = key;

    const wanted = new Map();
    for (let dx = -2; dx <= 2; dx++) {
      for (let dz = -2; dz <= 2; dz++) {
        const tx = cx + dx;
        const tz = cz + dz;
        for (const def of this._tileDeposits(tx, tz)) {
          if (this._depleted.has(def.id)) continue;  // already mined out → never respawn
          wanted.set(def.id, def);
        }
      }
    }

    // Despawn old
    for (const [id, dep] of Array.from(this._deposits.entries())) {
      if (!wanted.has(id)) {
        this._destroyDeposit(dep);
        this._deposits.delete(id);
      }
    }

    // Spawn new (respecting global cap)
    for (const [id, def] of wanted.entries()) {
      if (this._deposits.has(id)) continue;
      if (this._deposits.size >= MAX_DEPOSITS) break;
      const dep = this._createDeposit(def);
      if (dep) this._deposits.set(id, dep);
    }
  }

  _tileDeposits(tx, tz) {
    const cacheKey = `${tx}:${tz}`;
    if (this._tileCache.has(cacheKey)) return this._tileCache.get(cacheKey);

    // If the element catalog hasn't arrived yet, do NOT cache — otherwise we'd
    // freeze every tile as empty before deposits ever spawned.
    const elements = this.getElements?.();
    if (!elements || elements.length === 0) return [];

    // The body MUST have been scanned to reveal mineable deposits. If no
    // scans have ever been performed on this planet/moon, return nothing —
    // and DO NOT cache, so deposits appear immediately once a scan completes
    // and the body is reloaded.
    const scanned = this.getScannedElementNames?.() || [];
    if (scanned.length === 0) return [];

    const seed = (this.getBodySeed() | 0);
    const bodyType = this.getBodyType();
    const density = BIOME_DENSITY[bodyType] ?? BIOME_DENSITY.Default;
    const baseR = hash2(tx, tz, seed ^ 0xcafe);
    // Expected: 0–4 deposits per tile, scaled by density. With density 1.4+
    // and the smaller tile size, players see ~2-3 deposits per ~140m on
    // average — much more findable.
    let count = Math.floor(baseR * 5 * density);
    if (count > 4) count = 4;
    if (count <= 0) { this._tileCache.set(cacheKey, []); return []; }

    const out = [];
    for (let i = 0; i < count; i++) {
      const idSeed = seed ^ (i * 2654435761);
      const px = tx * TILE_SIZE + hash2(tx, tz, idSeed) * TILE_SIZE;
      const pz = tz * TILE_SIZE + hash2(tz, tx, idSeed ^ 0xa57) * TILE_SIZE;
      // Cull deposits inside the safe radius around the ship spawn (0,0)
      if (Math.hypot(px, pz) < SAFE_SHIP_RADIUS) continue;

      const element = this._chooseElement(tx, tz, idSeed);
      if (!element) continue;
      const tier = RARITY_TIER[element.rarity] || 1;
      // Yield 2-5 units; rarer elements yield slightly less per deposit
      const yieldUnits = Math.max(1, Math.floor(2 + hash2(tx, tz, idSeed ^ 0x33) * 4 - (tier - 1)));

      out.push({
        id: `d_${tx}_${tz}_${i}`,
        pos: new Vector3(px, 0, pz),
        element,
        tier,
        totalUnits: yieldUnits,
        remainingUnits: yieldUnits,
      });
    }
    this._tileCache.set(cacheKey, out);
    return out;
  }

  _chooseElement(tx, tz, seed) {
    const elements = this.getElements?.();
    if (!elements || elements.length === 0) return null;
    const bodyType = this.getBodyType();
    const poolSymbols = BIOME_POOLS[bodyType] || BIOME_POOLS.Default;
    // Restrict to elements actually detected on this body by prior scans —
    // every deposit on the surface is reflected in the scan results.
    const scannedNames = new Set(this.getScannedElementNames?.() || []);
    if (scannedNames.size === 0) return null;
    // Start by intersecting the catalog with the planet's scan results, then
    // bias the choice toward biome-appropriate elements while still allowing
    // any scanned element to surface (so rare "new" elements from the lore
    // are reachable even if they're outside the biome's nominal pool).
    let pool = elements.filter((e) => scannedNames.has(e.name));
    if (pool.length === 0) return null;
    // Among scanned elements, prefer those in the biome pool — give them a
    // 6× weight bonus when both lists overlap; otherwise everything stays
    // selectable (weighted purely by rarity).
    const biomeSet = new Set(poolSymbols);
    const weights = pool.map((e) => {
      let w;
      switch (e.rarity) {
        case 'Common': w = 10; break;
        case 'Uncommon': w = 6; break;
        case 'Rare': w = 2.5; break;
        case 'Ultra Rare': w = 1.2; break;
        case 'Legendary': w = 0.5; break;
        case 'Mythic': w = 0.15; break;
        default: w = 1;
      }
      if (biomeSet.has(e.symbol)) w *= 6;
      return w;
    });
    const total = weights.reduce((a, b) => a + b, 0);
    const pick = hash2(tx, tz, seed ^ 0xf1ee) * total;
    let acc = 0;
    for (let i = 0; i < pool.length; i++) {
      acc += weights[i];
      if (pick <= acc) return pool[i];
    }
    return pool[pool.length - 1];
  }

  _createDeposit(def) {
    const gy = this.getTerrainHeight(def.pos.x, def.pos.z);
    const pos = new Vector3(def.pos.x, gy + 0.9, def.pos.z);
    const tier = def.tier || 1;
    const radius = 0.65 + tier * 0.22;
    // Icosahedron (subdivisions=1) gives the angular crystalline look.
    const mesh = MeshBuilder.CreatePolyhedron(`deposit_${def.id}`, {
      type: 2, // icosahedron
      size: radius,
    }, this.scene);
    const color = hexToColor3(RARITY_COLOR[def.element.rarity] || '#9ca3af');
    const mat = new StandardMaterial(`depositMat_${def.id}`, this.scene);
    mat.diffuseColor = color.scale(0.55);
    mat.emissiveColor = color.scale(0.95);
    mat.specularColor = new Color3(0.4, 0.4, 0.4);
    mat.fogEnabled = true;
    mesh.material = mat;
    mesh.position = pos.clone();
    mesh.parent = this._depositParent;
    mesh.isPickable = false;
    mesh.rotation.y = (def.id.length * 11) % 360 * (Math.PI / 180);

    // Tall vertical beacon so the deposit is visible from far away across
    // uneven terrain. Thin emissive cylinder + small glowing cap.
    const beaconHeight = BEACON_HEIGHT * (0.85 + tier * 0.15);
    const beacon = MeshBuilder.CreateCylinder(`beacon_${def.id}`, {
      height: beaconHeight,
      diameter: 0.18,
      tessellation: 8,
    }, this.scene);
    const beaconMat = new StandardMaterial(`beaconMat_${def.id}`, this.scene);
    beaconMat.disableLighting = true;
    beaconMat.emissiveColor = color.scale(1.1);
    beaconMat.specularColor = new Color3(0, 0, 0);
    beaconMat.fogEnabled = true;
    beaconMat.alpha = 0.72;
    beacon.material = beaconMat;
    beacon.position = new Vector3(pos.x, gy + beaconHeight / 2, pos.z);
    beacon.parent = this._depositParent;
    beacon.isPickable = false;

    // Small bright "cap" sphere at the top of the beacon
    const cap = MeshBuilder.CreateSphere(`beaconCap_${def.id}`, {
      diameter: 0.55,
      segments: 8,
    }, this.scene);
    const capMat = new StandardMaterial(`beaconCapMat_${def.id}`, this.scene);
    capMat.disableLighting = true;
    capMat.emissiveColor = color.scale(1.6);
    capMat.fogEnabled = false;
    cap.material = capMat;
    cap.position = new Vector3(pos.x, gy + beaconHeight + 0.3, pos.z);
    cap.parent = this._depositParent;
    cap.isPickable = false;

    return {
      ...def,
      pos,
      mesh,
      mat,
      beacon,
      cap,
      _bobBase: pos.y,
      _bobPhase: hash2(0, 0, def.id.charCodeAt(2) | 0) * Math.PI * 2,
    };
  }

  _destroyDeposit(d) {
    try { d.mesh?.dispose(); } catch (_e) { /* noop */ }
    try { d.beacon?.dispose(); } catch (_e) { /* noop */ }
    try { d.cap?.dispose(); } catch (_e) { /* noop */ }
    if (this._activeDeposit === d) this._endMining(false);
  }
}

export default MiningSystem;
